import { Action, createReducer, on } from "@ngrx/store";
import { VIEW_ORDER_ALL, VIEW_ORDER_VOID } from "src/app/shared/const/views.const";
import { OrderReducer } from "src/app/shared/interfaces/order.interface";
import * as actions from '../actions/order.action';
import { buyerType } from "src/app/shared/const/options";
import { howSellValidationInitialState, initialOrderString, purchaseMethodValidationInitialState, rucaAndSisaInitialState, validationInitialState } from "src/app/shared/const/orders";
import { orderInitialState } from "@shared-logic/orders";

export const initialState: OrderReducer = {
    orderBoard: {
        all: [],
        allPipe: [],
        allPreviusOrders: [],
        buyerType: buyerType.COMMODITIES,
        filter: initialOrderString,
        indexFilter: -1,
        ordersSelected: [],
        isOrderReturned: false,
        orderDetails: orderInitialState,
        showOrderDetails: false
    },
    generateOrder: orderInitialState,
    //----------------\\
    generateOrders: [orderInitialState],
    indexToEdit: 0,
    numOfOrdersToGenerate: 1,
    //----------------\\
    validation: validationInitialState,
    rucaAndSisa: rucaAndSisaInitialState,
    howSellValidation: howSellValidationInitialState,
    purchaseMethodValidation: purchaseMethodValidationInitialState,
    validPxQ: '',
    edit: {
        ordersEdit: [],
        indexToEdit: 0,
        editAll: false,
        isGenerateEraser: true,
    },
    loaderSendToIBM: 0,

    viewsOfOrderBoard: {
        views: [],
        newView: VIEW_ORDER_VOID,
        indexViewSelected: -2,
        viewSelected: {
            ...VIEW_ORDER_ALL,
            viewName: 'default'
        }
    },
    historical: '',
    applyChangesToAll: true
};

const _orderReducer = createReducer(
    initialState,

    //Order to generate
    on(actions.setOrderToGenerate, (state, action) => {
        let newGenerateOrders = [...state.generateOrders];

        // Caso 1: Aplicar cambios a TODAS las órdenes del lote
        if (state.applyChangesToAll) {
            // Verificamos si la orden que llega NO es una de las del array (es decir, viene del formulario)
            const isFromForm = !state.generateOrders.some(o => o === action.order);

            if (isFromForm) {
                newGenerateOrders = state.generateOrders.map(o => {
                    // Si tenemos tags, aplicamos el cambio de forma INCREMENTAL
                    if (action.tag) {
                        const tag = action.tag;
                        const subTag = action.subTag;
                        const thirdTag = action.thirdTag;

                        // Clonamos la orden para modificarla
                        let updatedOrder: any = JSON.parse(JSON.stringify(o));
                        const sourceOrder: any = action.order;

                        if (thirdTag && subTag) {
                            if (!updatedOrder[tag as any]) updatedOrder[tag as any] = {};
                            if (!updatedOrder[tag as any][subTag as any]) updatedOrder[tag as any][subTag as any] = {};
                            updatedOrder[tag as any][subTag as any][thirdTag as any] = sourceOrder[tag as any][subTag as any][thirdTag as any];
                        } else if (subTag) {
                            if (!updatedOrder[tag as any]) updatedOrder[tag as any] = {};
                            updatedOrder[tag as any][subTag as any] = sourceOrder[tag as any][subTag as any];
                        } else {
                            updatedOrder[tag as any] = sourceOrder[tag as any];
                        }

                        // CRITICAL: El _id del precio debe ser el original de cada orden
                        if (o.price?._id) {
                            updatedOrder.price = { ...updatedOrder.price, _id: o.price._id };
                        }

                        return updatedOrder as any;
                    }

                    // Fallback: Mantenemos el comportamiento anterior si no hay tags, pero siendo cuidadosos
                    return {
                        ...action.order,
                        price: {
                            ...action.order.price,
                            _id: o.price?._id
                        }
                    };
                });
            }
        } else {
            // Caso 2: Aplicar cambios SOLO a la orden actualmente seleccionada por su índice
            if (state.indexToEdit >= 0 && state.indexToEdit < newGenerateOrders.length) {
                newGenerateOrders[state.indexToEdit] = action.order;
            }
        }

        const updatedCurrentOrder =
            state.indexToEdit >= 0 && state.indexToEdit < newGenerateOrders.length
                ? newGenerateOrders[state.indexToEdit]
                : action.order;

        // En edición por campo (tag), el formulario debe reflejar el snapshot que se está editando.
        // Para flujos sin tag, usamos la orden efectiva almacenada por índice.
        const nextGenerateOrder = action.tag ? action.order : updatedCurrentOrder;

        return {
            ...state,
            generateOrder: nextGenerateOrder,
            generateOrders: newGenerateOrders
        }
    }),
    on(actions.setPriceToGenerateInOrder, (state, action) => {
        let newGenerateOrders = [...state.generateOrders];

        if (state.applyChangesToAll) {
            newGenerateOrders = state.generateOrders.map(o => {
                return {
                    ...o,
                    price: {
                        ...action.price,
                        _id: o.price?._id // Preservamos el _id del tablero
                    }
                };
            });
        } else {
            if (state.indexToEdit >= 0 && state.indexToEdit < newGenerateOrders.length) {
                newGenerateOrders[state.indexToEdit] = {
                    ...newGenerateOrders[state.indexToEdit],
                    price: action.price
                };
            }
        }

        return {
            ...state,
            generateOrder: {
                ...state.generateOrder,
                price: action.price
            },
            generateOrders: newGenerateOrders
        }
    }),

    on(actions.setCommisionInOrder, (state, action) => {
        let newGenerateOrders = [...state.generateOrders];

        if (state.applyChangesToAll) {
            newGenerateOrders = state.generateOrders.map(o => {
                return {
                    ...o,
                    commision: action.commission
                };
            });
        } else {
            if (state.indexToEdit >= 0 && state.indexToEdit < newGenerateOrders.length) {
                newGenerateOrders[state.indexToEdit] = {
                    ...newGenerateOrders[state.indexToEdit],
                    commision: action.commission
                };
            }
        }

        return {
            ...state,
            generateOrder: {
                ...state.generateOrder,
                commision: action.commission
            },
            generateOrders: newGenerateOrders
        }
    }),

    on(actions.setRestriction, (state, action) => {
        let newGenerateOrders = [...state.generateOrders];

        if (state.applyChangesToAll) {
            newGenerateOrders = state.generateOrders.map(o => {
                return {
                    ...o,
                    buyer: action.restriction
                };
            });
        } else {
            if (state.indexToEdit >= 0 && state.indexToEdit < newGenerateOrders.length) {
                newGenerateOrders[state.indexToEdit] = {
                    ...newGenerateOrders[state.indexToEdit],
                    buyer: action.restriction
                };
            }
        }

        return {
            ...state,
            generateOrder: {
                ...state.generateOrder,
                buyer: action.restriction
            },
            generateOrders: newGenerateOrders
        }
    }),

    on(actions.setValidation, (state, action) => {
        return {
            ...state,
            validation: action.validation
        }
    }),
    on(actions.setRucaAndSisa, (state, action) => {
        return {
            ...state,
            rucaAndSisa: action.rucaAndSisa
        }
    }),
    on(actions.setHowSellValidation, (state, action) => {
        return {
            ...state,
            howSellValidation: action.howSellValidation
        }
    }),
    on(actions.setPurchaseMethodValidationState, (state, action) => {
        return {
            ...state,
            purchaseMethodValidation: action.purchaseMethodValidation
        }
    }),
    on(actions.setValidPxQ, (state, action) => {
        return {
            ...state,
            validPxQ: action.validPxQ
        }
    }),


    on(actions.resetOrderToGenerate, (state, action) => {
        return {
            ...state,
            generateOrder: initialState.generateOrder
        }
    }),

    on(actions.setNumOfDuplicateOrders, (state, action) => {
        return {
            ...state,
            numOfOrdersToGenerate: action.numOfDuplicateOrders
        }
    }),

    on(actions.setOrdersToGenerate, (state, action) => {
        return {
            ...state,
            generateOrders: action.orders
        }
    }),

    on(actions.setIndexToEdit, (state, action) => {
        return {
            ...state,
            indexToEdit: action.indexToEdit
        }
    }),


    on(actions.loadOrdersAllSuccess, (state, action) => {
        return {
            ...state,
            orderBoard: {
                ...state.orderBoard,
                all: action.orders
            }

        };
    }),

    on(actions.loadOrdersAllPrevius, (state, action) => {
        return {
            ...state,
            orderBoard: {
                ...state.orderBoard,
                allPreviusOrders: action.orders
            }
        }
    }),

    on(actions.loadOrdersAllWithPipesSuccess, (state, action) => {
        return {
            ...state,
            orderBoard: {
                ...state.orderBoard,
                allPipe: action.orders
            }
        }
    }),

    //Is order returned
    on(actions.setIsOrderReturned, (state, action) => {
        return {
            ...state,
            orderBoard: {
                ...state.orderBoard,
                isOrderReturned: action.isOrderReturned
            }

        };
    }),



    ////----------------------FILTERS------------------------\\\\

    on(actions.setFilterOrder, (state, action) => {
        return {
            ...state,
            orderBoard: {
                ...state.orderBoard,
                filter: action.orderString
            }

        };
    }),
    on(actions.setBuyerTypeFilter, (state, action) => {
        return {
            ...state,
            orderBoard: {
                ...state.orderBoard,
                buyerType: action.buyerType
            }

        };
    }),

    on(actions.setFilterIndex, (state, action) => {
        return {
            ...state,
            orderBoard: {
                ...state.orderBoard,
                indexFilter: action.index
            }
        };
    }),
    ////----------------------VIEWS------------------------\\\\

    on(actions.setViewToGenerateO, (state, action) => {
        return {
            ...state,
            viewsOfOrderBoard: {
                ...state.viewsOfOrderBoard,
                newView: action.view
            }
        }
    }),
    on(actions.resetNewViewO, (state, action) => {
        return {
            ...state,
            viewsOfOrderBoard: {
                ...state.viewsOfOrderBoard,
                newView: initialState.viewsOfOrderBoard.newView
            }
        }
    }),
    on(actions.setViewSelectedO, (state, action) => {
        return {
            ...state,
            viewsOfOrderBoard: {
                ...state.viewsOfOrderBoard,
                viewSelected: action.view
            }
        }
    }),
    on(actions.resetViewSelectedO, (state, action) => {
        return {
            ...state,
            viewsOfOrderBoard: {
                ...state.viewsOfOrderBoard,
                viewSelected: initialState.viewsOfOrderBoard.viewSelected
            }
        }
    }),
    on(actions.setAllViewsO, (state, action) => {
        return {
            ...state,
            viewsOfOrderBoard: {
                ...state.viewsOfOrderBoard,
                views: action.views
            }
        }
    }),
    on(actions.setIndexViewSelected, (state, action) => {
        return {
            ...state,
            viewsOfOrderBoard: {
                ...state.viewsOfOrderBoard,
                indexViewSelected: action.index
            }
        }
    }),

    //----------------------ORDERS SELECTED----------------------\\
    //Select prices
    on(actions.setOrdersSelected, (state, action) => {
        return {
            ...state,
            orderBoard: {
                ...state.orderBoard,
                ordersSelected: action.orders
            }
        }
    }),


    //---------------------Orders Edit------------------------\\
    on(actions.setValidationOrdersToGenerate, (state, action) => {
        return {
            ...state,
            edit: {
                ...state.edit,
                ordersEdit: action.orders
            }
        }
    }),

    on(actions.setIndexToEditValidation, (state, action) => {
        return {
            ...state,
            edit: {
                ...state.edit,
                indexToEdit: action.indexToEdit
            }
        }
    }),
    on(actions.setEditAll, (state, action) => {
        return {
            ...state,
            edit: {
                ...state.edit,
                editAll: action.editAll
            }
        }
    }),
    on(actions.isGenerateEraser, (state, action) => {
        return {
            ...state,
            edit: {
                ...state.edit,
                isGenerateEraser: action.isGenerateEraser
            }
        }
    }),

    //Price details
    on(actions.setOrderDetails, (state, action) => {
        return {
            ...state,
            orderBoard: {
                ...state.orderBoard,
                orderDetails: action.order
            }
        }
    }),
    on(actions.showOrderDetails, (state, action) => {
        return {
            ...state,
            orderBoard: {
                ...state.orderBoard,
                showOrderDetails: action.show
            }
        }
    }),

    //Loader send to IBM
    on(actions.setLoaderSendToIBM, (state, action) => {
        return {
            ...state,
            loaderSendToIBM: action.loader
        }
    }),
    on(actions.addLoaderSendToIBM, (state, action) => {
        return {
            ...state,
            loaderSendToIBM: state.loaderSendToIBM + 1
        }
    }),
    on(actions.discountLoaderSendToIBM, (state, action) => {
        return {
            ...state,
            loaderSendToIBM: state.loaderSendToIBM - 1
        }
    }),

    on(actions.setHistorical, (state, action) => {
        return {
            ...state,
            historical: action.historical
        }
    }),

    on(actions.setApplyChangesToAll, (state, action) => {
        return {
            ...state,
            applyChangesToAll: action.applyChangesToAll
        }
    }),
)


export function orderReducer(state: any, action: Action) {
    return _orderReducer(state, action);
}

